import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { DeltaByTenor } from '@approot/models/delta-by-tenor';
import { PricingData } from '@approot/models/strategy-payload';
import {
  AlertsService,
  LegService,
  PricingExtraService
} from '@approot/services';
import {
  exponentialDelay,
  shortUuid,
  sleep,
  SubscriptionSink,
  Utilities
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import copy from 'copy-to-clipboard';
import { fromEvent } from 'rxjs';

@Component({
  selector: 'app-delta-by-tenor',
  templateUrl: './delta-by-tenor.component.html',
  styleUrls: ['./delta-by-tenor.component.scss']
})
export class DeltaByTenorComponent implements OnInit, OnDestroy {
  @Output() deltaByTenorOpenChanged = new EventEmitter<boolean>();
  @Input() isModalOpened = false;
  @Input() legComponents: LegComponent[];
  activeCurrencyPairs: string[] = [];
  currencySelected: string;
  defaultValue: string;
  fetchingResponse: boolean;
  errorMessage: string;
  pricingData: PricingData = {};
  data: DeltaByTenor[] = [];
  private subSink = new SubscriptionSink();
  private websocketUUID = 'delta-by-tenor_' + shortUuid();

  constructor(
    private legService: LegService,
    private elementRef: ElementRef,
    private webSocketService: GlobalWebsocketService,
    private alertsService: AlertsService,
    private pricingExtraService: PricingExtraService
  ) {}

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public ngOnInit(): void {
    this.legComponents.map((x: any) => {
      // there can't be duplicates of each currencyPair, so check if it already exists in the array before adding it.
      if (
        x.form.get('cross') &&
        x.form.get('cross').value &&
        this.activeCurrencyPairs.indexOf(x.form.get('cross').value) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.get('cross').value);
      }

      if (
        x.form.get('secondCross') &&
        x.form.get('secondCross').value &&
        this.activeCurrencyPairs.indexOf(x.form.get('secondCross').value) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.get('secondCross').value);
      }
    });

    if (this.activeCurrencyPairs.length === 1) {
      // if only one currencyPair then just make the default value be the value available
      this.defaultValue = this.activeCurrencyPairs[0];
    }

    this.subSink.sink = fromEvent(document, 'keydown').subscribe(event => {
      // we want to listen to the f9 button press
      this.keypress(event);
    });

    this.webSocketListener();
  }

  private webSocketListener(): void {
    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        this.fetchingResponse = false;
        this.errorMessage =
          'There was a problem setting up the websocket for the delta by tenor';
        return;
      }
      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.websocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            if (resp.error) {
              this.alertsService.addAlert({
                message: resp.error.message,
                type: 'error'
              });
              this.dataError();
            } else {
              this.handleDeltaByTenorPayload(resp.payload as DeltaByTenor[]);
            }
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleDeltaByTenorPayload(payload: DeltaByTenor[]): void {
    // Sometimes get a result "{error: undefined}", often "after" the actual response.
    // This might be because of unexpected "pong" messages.  Those are now
    // filtered in the websocket service, but belt-and-braces...
    if (Array.isArray(payload)) {
      this.fetchingResponse = false;
      this.data = payload as DeltaByTenor[];
    } else {
      console.warn('Delta by tenor response is not an array', payload);
      this.dataError();
    }
  }

  dataError() {
    this.fetchingResponse = false;
    this.errorMessage = 'Failed to load data';
  }

  keypress(event) {
    if (event.keyCode === 120 && !this.fetchingResponse) {
      // if the key is f9 and the forms are valid, calculate the value
      this.onCurrencyPairChange(this.currencySelected); //
    }
  }

  modalChanged(event) {
    this.deltaByTenorOpenChanged.emit(event);
  }

  formatTableForCopy() {
    const table = this.elementRef.nativeElement.querySelector('#table-data');

    return Utilities.tableToExcelFormat(table);
  }

  onCopyToClipboardClicked() {
    copy(this.formatTableForCopy(), { format: 'text/plain' }); // copy the text. keep in text/plain, so that tabs and new lines stay
  }

  onCurrencyPairChange(currencyPair: string) {
    this.currencySelected = currencyPair;
    if (currencyPair) {
      const cross =
        currencyPair.substring(0, 3) + '/' + currencyPair.substring(3, 6);
      let task = {
        taskType: 'deltaByTenor',
        taskParameters: {
          cross
        }
      };

      // join all the market data from all the legs
      this.pricingData = {
        fixings: [],
        treasuryRates: [],
        marketData: []
      };
      // filtered to only have fixings for the desired cuttoff/fixing-source
      let filteredRequiredFixings = [];

      // fixings
      this.legComponents.forEach((legComponent: LegComponent) => {
        if (legComponent.legHelperService.requiredFixings) {
          let fixings = legComponent.legPricingService.getFixings();
          filteredRequiredFixings = fixings.concat(fixings);
        }
      });
      this.pricingData.fixings = filteredRequiredFixings;

      // treasury curves
      this.price(task);
    } else {
      this.data = []; // if no select menu, then remove currency pair selected.
    }
  }

  price(task) {
    // add all of the selected legs
    let payload = this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      this.legComponents.map(y => {
        return { product: y.form.value, attributes: y.leg.attributes };
      }),
      task,
      this.pricingData
    );

    this.fetchingResponse = true; // make the spinner show
    this.errorMessage = ''; // wipe these so a message and no data appears
    this.data = [];

    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}pricing`,
      payload,
      uuid: this.websocketUUID
    });
  }
}
